import { useMemo, useState } from 'react';
import {
  Activity,
  AlertTriangle,
  MapPin,
  Search,
  UserCheck,
  UserX,
} from 'lucide-react';
import { FLEET_READINESS, STATUS_CONFIG } from './mockAvailabilityData';

function StatusBadge({ status }) {
  const config = STATUS_CONFIG[status];
  return (
    <span
      className={`inline-flex rounded-full px-2 py-0.5 text-[9px] font-bold uppercase leading-tight tracking-wide ring-1 ${config.className}`}
    >
      {config.label}
    </span>
  );
}

function DriverStatusCell({ status }) {
  const isAvailable = status === 'Checked-In';
  const isOnTrip = status === 'On Trip';
  const isRest = status === 'Rest-Period';
  const isMissing = status === 'No Driver' || status === 'Unassigned';

  return (
    <span
      className={`inline-flex items-center gap-1 text-[11px] font-medium ${
        isAvailable
          ? 'text-emerald-700'
          : isOnTrip
            ? 'text-blue-700'
            : isRest
              ? 'text-amber-700'
              : isMissing
                ? 'text-logi-muted'
                : 'text-logi-muted'
      }`}
    >
      {isAvailable && <UserCheck className="h-3 w-3 shrink-0" />}
      {isMissing && <UserX className="h-3 w-3 shrink-0" />}
      {status}
    </span>
  );
}

export default function FleetReadinessTable({
  fleet = FLEET_READINESS,
  statusFilter = null,
}) {
  const [query, setQuery] = useState('');

  const filtered = useMemo(() => {
    return fleet.filter((v) => {
      const matchesStatus = !statusFilter || v.status === statusFilter;
      const q = query.toLowerCase();
      const matchesQuery =
        !q ||
        v.vehicleNumber.toLowerCase().includes(q) ||
        v.vehicleType.toLowerCase().includes(q) ||
        v.location.toLowerCase().includes(q) ||
        v.driverName.toLowerCase().includes(q) ||
        STATUS_CONFIG[v.status].label.toLowerCase().includes(q);
      return matchesStatus && matchesQuery;
    });
  }, [fleet, statusFilter, query]);

  const dispatchBlocked = filtered.filter(
    (v) => v.status === 'maintenance' || v.status === 'expired_documents',
  ).length;

  return (
    <section className="overflow-hidden rounded-xl border border-logi-grid bg-white">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-logi-grid px-4 py-3">
        <div className="flex items-center gap-2">
          <Activity className="h-4 w-4 text-logi-accent" strokeWidth={1.75} />
          <div>
            <h3 className="text-sm font-bold text-logi-text">Real-Time Fleet Readiness Tracker</h3>
            <p className="text-[11px] text-logi-muted">
              {filtered.length} vehicles · Instant dispatch query
            </p>
          </div>
        </div>
        <div className="relative min-w-0 flex-1 sm:max-w-xs">
          <Search className="absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-logi-muted" />
          <input
            type="search"
            placeholder="Search vehicle, driver, location…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="w-full rounded-lg border border-logi-grid bg-white py-2 pl-9 pr-3 text-sm text-logi-text placeholder-logi-subtle outline-none focus:border-logi-accent/50 focus:ring-1 focus:ring-logi-accent/30"
          />
        </div>
      </div>

      {dispatchBlocked > 0 && (
        <div className="flex items-center gap-2 border-b border-red-200 bg-red-50 px-4 py-2 text-xs text-red-700">
          <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
          {dispatchBlocked} vehicle(s) grounded — do not dispatch (Maintenance / Expired Docs)
        </div>
      )}

      <table className="w-full table-fixed text-left text-sm">
        <colgroup>
          <col className="w-[24%]" />
          <col className="w-[32%]" />
          <col className="w-[18%]" />
          <col className="w-[26%]" />
        </colgroup>
        <thead>
          <tr className="border-b border-logi-grid bg-logi-silver text-[10px] uppercase tracking-wider text-logi-muted">
            <th className="px-3 py-2.5 font-semibold">Vehicle</th>
            <th className="px-3 py-2.5 font-semibold">Location</th>
            <th className="px-3 py-2.5 font-semibold">Status</th>
            <th className="px-3 py-2.5 font-semibold">Driver</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-logi-grid">
          {filtered.map((row) => {
            const isMuted = STATUS_CONFIG[row.status].rowMuted;

            return (
              <tr
                key={row.id}
                className={`align-top transition-colors ${
                  isMuted ? 'bg-red-50/80 opacity-90' : 'hover:bg-logi-silver/60'
                }`}
              >
                <td className="px-3 py-2.5">
                  <p className="font-mono text-xs font-semibold text-logi-accent">
                    {row.vehicleNumber}
                    {isMuted && (
                      <AlertTriangle className="ml-1 inline h-3 w-3 text-red-600" />
                    )}
                  </p>
                  <p className="mt-0.5 text-[11px] text-logi-muted">{row.vehicleType}</p>
                  <p className="mt-0.5 font-mono text-[10px] font-semibold text-amber-700">
                    {row.capacity}
                  </p>
                </td>
                <td className="px-3 py-2.5">
                  <span className="inline-flex items-start gap-1 text-[11px] leading-snug text-logi-muted">
                    <MapPin className="mt-0.5 h-3 w-3 shrink-0 text-logi-subtle" />
                    <span className="break-words">{row.location}</span>
                  </span>
                  {row.etaMinutes ? (
                    <p className="mt-1 text-[10px] font-medium text-teal-700">
                      ETA free: ~{Math.floor(row.etaMinutes / 60)}h {row.etaMinutes % 60}m
                    </p>
                  ) : null}
                </td>
                <td className="px-3 py-2.5">
                  <StatusBadge status={row.status} />
                </td>
                <td className="px-3 py-2.5">
                  <p className="truncate text-xs font-medium text-logi-text">{row.driverName}</p>
                  <div className="mt-1">
                    <DriverStatusCell status={row.driverStatus} />
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      {filtered.length === 0 && (
        <p className="px-4 py-8 text-center text-sm text-logi-muted">
          No vehicles match your search or filter.
        </p>
      )}
    </section>
  );
}
